import { useEffect, useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type ConsoleMethod = "GET" | "POST" | "PUT" | "DELETE";

export type ConsoleEndpoint = {
  idx: number;
  m: ConsoleMethod;
  path: string;
  hasBody: boolean;
  hasQuery: boolean;
  note: string;
  mutating?: boolean;
};

export type ConsoleProfile = {
  label: string;
  baseUrl: string;
  endpoints: ConsoleEndpoint[];
  defaultBodies: Record<string, string>;
  defaultQueries: Record<string, string>;
  probePath?: string;
};

export type ConsoleProxyBody = {
  method: string;
  path: string;
  query?: Record<string, string>;
  body?: unknown;
};

export type ConsoleProxyResult = {
  code: number;
  message: string;
  data?: unknown;
  upstream_status: number;
};

export type ConsoleProbeResult = { data: unknown; toast: string };

function CopyButton({ value, notify }: { value: string; notify: (ok: boolean) => void }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="ml-auto inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          window.setTimeout(() => setDone(false), 1500);
          notify(true);
        } catch {
          notify(false);
        }
      }}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? "已复制" : "复制"}
    </button>
  );
}

function methodClass(method: ConsoleMethod): string {
  if (method === "GET") return "bg-blue-100 text-blue-800";
  if (method === "PUT") return "bg-amber-100 text-amber-800";
  if (method === "DELETE") return "bg-red-100 text-red-800";
  return "bg-violet-100 text-violet-800";
}

export function ApiTestConsole({
  title,
  adapterType,
  url,
  supported,
  unsupportedNote,
  profile,
  authHeaderLine,
  probe,
  proxy,
  note,
  onClose,
}: {
  title: string;
  adapterType: string;
  url: string;
  supported: boolean;
  unsupportedNote?: ReactNode;
  profile?: ConsoleProfile;
  authHeaderLine: string;
  probe?: () => Promise<ConsoleProbeResult>;
  proxy: (body: ConsoleProxyBody) => Promise<ConsoleProxyResult>;
  // note 是可选的补充说明（例如「当前渠道前缀由站点的业务模块路径决定」），
  // 渲染在标题下方，便于运营确认这次测的地址从哪来。
  note?: ReactNode;
  onClose: () => void;
}) {
  const endpoints = profile?.endpoints ?? [];
  const [current, setCurrent] = useState(0);
  const endpoint = endpoints[current];
  const [bodyText, setBodyText] = useState("");
  const [pathText, setPathText] = useState("");
  const [queryText, setQueryText] = useState("");
  const [requestPreview, setRequestPreview] = useState("");
  const [responseText, setResponseText] = useState("选择端点并点击「发送」以查看返回结果");
  const [statusText, setStatusText] = useState("—");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{ text: string; error: boolean } | null>(null);

  function flash(text: string, error = false) {
    setToast({ text, error });
    window.setTimeout(() => setToast(null), 2600);
  }

  function load(index: number) {
    if (!profile) return;
    const next = endpoints[index];
    setCurrent(index);
    setPathText(next.path);
    setBodyText(profile.defaultBodies[next.path] ?? "");
    setQueryText(profile.defaultQueries[next.path] ?? "");
    setRequestPreview(`${next.m} ${profile.baseUrl}${next.path}\n${authHeaderLine}\n\n(点击「发送」后展示最终请求)`);
    setResponseText("选择端点并点击「发送」以查看返回结果");
    setStatusText("—");
  }

  useEffect(() => {
    if (endpoints.length > 0) load(0);
    // profile 在单个抽屉实例内固定。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send() {
    if (!profile || !endpoint) return;
    if (/\{[^}]+\}/.test(pathText)) {
      flash("请先把路径占位符替换为真实 ID", true);
      return;
    }
    if (endpoint.hasBody && (/<[A-Z][A-Z0-9_]*>/.test(bodyText) || /REPLACE_WITH_[A-Z0-9_]+/.test(bodyText))) {
      flash("请先替换请求体中的示例占位值", true);
      return;
    }

    let body: unknown;
    if (endpoint.hasBody) {
      try {
        body = JSON.parse(bodyText);
      } catch (error) {
        flash("请求体不是合法 JSON: " + (error as Error).message, true);
        return;
      }
    }
    let query: Record<string, string> | undefined;
    if (endpoint.hasQuery) {
      try {
        const parsed = JSON.parse(queryText || "{}");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("query 需为 JSON 对象");
        query = Object.fromEntries(
          Object.entries(parsed)
            .filter(([, value]) => value !== "" && value != null)
            .map(([key, value]) => [key, String(value)]),
        );
      } catch (error) {
        flash("query 不是合法 JSON 对象: " + (error as Error).message, true);
        return;
      }
    }
    if (endpoint.mutating && !window.confirm(`确认执行真实写操作？\n站点：${title}\n请求：${endpoint.m} ${pathText}\n\n该操作会改变下游数据，且不会自动重试。`)) {
      return;
    }

    const queryString = query && Object.keys(query).length > 0 ? `?${new URLSearchParams(query)}` : "";
    setRequestPreview(
      `${endpoint.m} ${profile.baseUrl}${pathText}${queryString}\n${authHeaderLine}${endpoint.hasBody ? `\nContent-Type: application/json\n\n${JSON.stringify(body, null, 2)}` : ""}`,
    );
    setSending(true);
    setStatusText("等待");
    try {
      if (profile.probePath === pathText && probe) {
        const result = await probe();
        setStatusText("200 / 0");
        setResponseText(JSON.stringify(result.data, null, 2));
        flash(result.toast);
      } else {
        const result = await proxy({ method: endpoint.m, path: pathText, query, body });
        setStatusText(`${result.upstream_status} / ${result.code}`);
        setResponseText(JSON.stringify(result, null, 2));
        flash(result.code === 0 ? "发送成功" : result.message || "上游返回失败", result.code !== 0);
      }
    } catch (error) {
      const e = error as { message?: string; body?: unknown; status?: number };
      setStatusText(e.status ? `HTTP ${e.status}` : "error");
      setResponseText(JSON.stringify(e.body ?? { message: e.message ?? String(error) }, null, 2));
      flash(e.message ?? "请求失败", true);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="flex h-full w-full max-w-5xl flex-col bg-card shadow-xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              <Badge variant={supported ? "success" : "muted"}>{supported ? "已接入" : "未接入"}</Badge>
              <span className="ml-2 font-mono">{adapterType}</span><span className="ml-3">{url}</span>
            </p>
            {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>× 关闭</Button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {!supported && <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">{unsupportedNote ?? "该适配器未接入接口测试控制台"}</div>}
          {supported && endpoint && profile && (
            <>
              <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900"><b>{profile.label}</b> — 凭证由服务端注入，页面仅显示脱敏预览。</div>
              <div className="flex flex-wrap gap-1.5">
                {endpoints.map((item) => (
                  <button key={item.idx} type="button" onClick={() => load(item.idx)} className={`rounded-lg border px-3 py-1.5 text-xs ${current === item.idx ? "border-primary bg-foreground text-background" : "bg-background hover:bg-accent"}`}>
                    <b>{item.m}</b> {item.path}{item.mutating ? " ⚠" : ""}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex h-8 w-16 items-center justify-center rounded-lg text-xs font-bold ${methodClass(endpoint.m)}`}>{endpoint.m}</span>
                <span className="rounded-lg border bg-muted px-3 py-2 font-mono text-xs">{profile.baseUrl}</span>
                <input className="h-8 min-w-[200px] flex-1 rounded-lg border px-3 font-mono text-xs" value={pathText} onChange={(event) => setPathText(event.target.value)} />
                <Button size="sm" onClick={send} disabled={sending}>{sending ? "发送中…" : "发送"}</Button>
              </div>
              <p className="text-xs text-muted-foreground">{endpoint.note}</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <section className="overflow-hidden rounded-xl border">
                  <header className="flex border-b bg-muted px-3 py-2 text-xs">REQUEST <CopyButton value={requestPreview} notify={(ok) => flash(ok ? "已复制请求" : "复制失败", !ok)} /></header>
                  {endpoint.hasQuery && <textarea className="min-h-32 w-full border-b p-3 font-mono text-xs" value={queryText} onChange={(event) => setQueryText(event.target.value)} aria-label="Query JSON" />}
                  {endpoint.hasBody && <textarea className="min-h-48 w-full border-b p-3 font-mono text-xs" value={bodyText} onChange={(event) => setBodyText(event.target.value)} aria-label="Body JSON" />}
                  <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words bg-slate-900 p-3 text-xs text-slate-100">{requestPreview}</pre>
                </section>
                <section className="overflow-hidden rounded-xl border">
                  <header className="flex border-b bg-muted px-3 py-2 text-xs">RESPONSE · {statusText} <CopyButton value={responseText} notify={(ok) => flash(ok ? "已复制响应" : "复制失败", !ok)} /></header>
                  <pre className="max-h-[520px] min-h-80 overflow-auto whitespace-pre-wrap break-words bg-slate-900 p-3 text-xs text-slate-100">{responseText}</pre>
                </section>
              </div>
            </>
          )}
        </div>
        {toast && <div className={`fixed bottom-6 right-6 z-[60] rounded-lg px-4 py-2 text-xs shadow-lg ${toast.error ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>{toast.text}</div>}
      </div>
    </div>
  );
}
